import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, Award, ArrowRight, Loader2, XCircle } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

const POLL_MAX = 10;
const POLL_INTERVAL_MS = 2000;

export default function PaymentSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState({ status: "polling", payment_status: "pending", plan_id: null });
  const [attempts, setAttempts] = useState(0);
  const pollingRef = useRef(false);

  const sessionId = new URLSearchParams(location.search).get("session_id");

  useEffect(() => {
    if (!sessionId) {
      navigate("/pricing");
      return;
    }
    if (pollingRef.current) return;
    pollingRef.current = true;

    let cancelled = false;
    let count = 0;

    const tick = async () => {
      if (cancelled) return;
      count += 1;
      setAttempts(count);
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setState(data);
        if (data.payment_status === "paid") {
          await refresh();
          return;
        }
        if (data.status === "expired") return;
        if (count < POLL_MAX) {
          setTimeout(tick, POLL_INTERVAL_MS);
        }
      } catch (e) {
        if (count < POLL_MAX) {
          setTimeout(tick, POLL_INTERVAL_MS);
        } else {
          setState({ status: "error", payment_status: "error" });
        }
      }
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [sessionId, navigate, refresh]);

  const paid = state.payment_status === "paid";
  const expired = state.status === "expired";
  const errored = state.status === "error";
  const stillWaiting = !paid && !expired && !errored;

  return (
    <div className="min-h-screen bg-white grid place-items-center px-6" data-testid="payment-success-page">
      <div className="max-w-lg w-full text-center">
        {paid && (
          <div data-testid="payment-paid-state">
            <div className="w-20 h-20 mx-auto bg-green-600 grid place-items-center rounded-sm platinum-glow">
              <Award className="w-10 h-10 text-white" strokeWidth={2} />
            </div>
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-green-700 mt-6">Payment confirmed</div>
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-3 text-slate-950">
              You're in.
            </h1>
            <p className="text-slate-600 mt-3">
              Your <strong className="text-slate-900 capitalize">{state.plan_id}</strong> plan is now active. All
              8 modules and certification quizzes are unlocked. Time to earn that credential.
            </p>
            <div className="mt-8 flex items-center justify-center gap-3">
              <Link
                to="/dashboard"
                data-testid="payment-go-dashboard-btn"
                className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-sm font-semibold inline-flex items-center gap-2"
              >
                Go to dashboard <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/courses"
                data-testid="payment-go-courses-btn"
                className="border border-slate-300 hover:bg-slate-50 text-slate-900 px-6 py-3 rounded-sm font-semibold"
              >
                Browse modules
              </Link>
            </div>
          </div>
        )}

        {stillWaiting && (
          <div data-testid="payment-pending-state">
            <Loader2 className="w-12 h-12 text-orange-600 mx-auto animate-spin" />
            <h1 className="font-display text-3xl font-bold tracking-tight mt-6 text-slate-950">
              Confirming your payment…
            </h1>
            <p className="text-slate-600 mt-3">
              Hang tight — Stripe is finalizing your transaction. Attempt {attempts} of {POLL_MAX}.
            </p>
          </div>
        )}

        {expired && (
          <div data-testid="payment-expired-state">
            <XCircle className="w-12 h-12 text-orange-600 mx-auto" />
            <h1 className="font-display text-3xl font-bold tracking-tight mt-6 text-slate-950">
              Session expired
            </h1>
            <p className="text-slate-600 mt-3">Your checkout session timed out. Please start a new one.</p>
            <Link to="/pricing" className="mt-6 inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-3 rounded-sm font-semibold">
              Back to pricing <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        {errored && (
          <div data-testid="payment-error-state">
            <XCircle className="w-12 h-12 text-red-600 mx-auto" />
            <h1 className="font-display text-3xl font-bold tracking-tight mt-6 text-slate-950">
              We couldn't confirm payment
            </h1>
            <p className="text-slate-600 mt-3">
              If you were charged, contact support — we'll fix it. Otherwise, please try again.
            </p>
            <Link to="/pricing" className="mt-6 inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-3 rounded-sm font-semibold">
              Try again <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        {paid && (
          <div className="mt-10 inline-flex items-center gap-2 text-xs text-slate-500">
            <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> Receipt sent by Stripe to your email.
          </div>
        )}
      </div>
    </div>
  );
}
